<template>
    <div class="ca-binding">
        <div class="page-header">
            <h2 class="page-title">绑定CA</h2>
        </div>

        <div class="page-content">
            <div class="binding-card" :class="{ bound: isBound, unbound: !isBound }">
                <div class="card-header">
                    <div class="card-header-left">
                        <div class="card-title">CA绑定状态</div>
                        <div class="card-subtitle">用于登录、签章、解密等业务操作</div>
                    </div>
                    <div class="status-badge" :class="{ bound: isBound, unbound: !isBound }">
                        {{ isBound ? '已绑定' : '未绑定' }}
                    </div>
                </div>

                <div class="card-body">
                    <div class="info-grid">
                        <div class="info-item">
                            <div class="label">绑定名称</div>
                            <div class="value">{{ bindName || '-' }}</div>
                        </div>

                        <div class="info-item">
                            <div class="label">证书信息</div>
                            <div class="value" :class="{ muted: !isBound }">
                                {{ isBound ? certInfoDisplay : '暂无绑定证书' }}
                            </div>
                        </div>
                    </div>

                    <div v-if="!isBound" class="select-row">
                        <div class="select-label">选择证书</div>
                        <el-select v-model="selectedCertId" placeholder="请选择证书" size="small" style="width: 360px;">
                            <el-option v-for="item in certList" :key="item.certid" :label="item.name"
                                :value="item.certid" />
                        </el-select>
                        <span v-if="!certList || certList.length === 0" class="select-tip">未检测到可用证书</span>
                    </div>

                    <div class="action-row">
                        <template v-if="isBound">
                            <el-button type="primary" size="small" @click="handleChange">更换证书</el-button>
                            <el-button type="danger" plain size="small" @click="handleUnbind">解绑</el-button>
                        </template>
                        <template v-else>
                            <el-button type="primary" size="small" :disabled="!selectedCertId"
                                @click="handleBind">立即绑定</el-button>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { bindCa } from '@/api/modules/user'
import Cookies from 'js-cookie'
import { useStore } from '@/api/ca/store/index'
import { XTXASynObject } from '@/api/ca/XTXASynObject'

export default {
    name: 'CaBinding',
    data() {
        return {
            bindName: Cookies.get('userName'),
            certInfo: null,
            store: null,
            xtxObject: null,
            certList: [],
            selectedCertId: ''
        }
    },
    computed: {
        isBound() {
            if (!this.certInfo) return false
            if (typeof this.certInfo === 'string') return this.certInfo.trim().length > 0
            if (typeof this.certInfo === 'object') return Object.keys(this.certInfo).length > 0
            return false
        },
        certInfoDisplay() {
            if (!this.certInfo) return ''
            if (typeof this.certInfo === 'string') return this.certInfo
            const certId = this.certInfo.certId || this.certInfo.certid || this.certInfo.serialNumber || ''
            const issuer = this.certInfo.issuer || ''
            const validTo = this.certInfo.validTo || this.certInfo.expireTime || ''

            const parts = []
            if (certId) parts.push(`证书号：${certId}`)
            if (issuer) parts.push(`颁发机构：${issuer}`)
            if (validTo) parts.push(`有效期至：${validTo}`)
            return parts.join('  |  ') || '-'
        }
    },
    mounted() {
        this.initPage()
    },
    methods: {
        //  初始化页面
        initPage() {
            this.store = useStore()
            this.xtxObject = new XTXASynObject()
            this.initCertList()
        },
        // 初始化证书列表
        async initCertList() {
            try {
                // 初始化WebSocket连接
                await this.xtxObject.initWebSocket()
                // 获取用户列表
                await this.xtxObject.SOF_GetUserList()
                // 等待一下，确保store已更新
                await this.$nextTick()
                // 获取证书列表
                const certList = this.store.state.XTXModel.ukeyList || []
                this.certList =  certList;
                if (this.certList.length > 0 && !this.isBound) {
                    this.selectedCertId = this.certList[0].certid
                }
            } catch (error) {
            console.error('请确保已安装证书助手并插入CA锁')
            }
        },
        // 绑定证书
        handleBind() {
            this.bindCa()
        },
        bindCa() {
            bindCa({
                cerInfo: this.selectedCertId,
                bindName: this.bindName
            }).then(() => {
                this.$message.success('绑定成功')
            }).catch((error) => {
                this.$message.error('绑定失败：' + (error.message || '请确保已安装证书助手并插入CA锁'))
            })

        },
        handleChange() {
            this.selectedCertId = ''
            this.certInfo = null
            if (this.certList && this.certList.length > 0) {
                this.selectedCertId = this.certList[0].certid
            }
        },
        handleUnbind() {
            this.$confirm('确认解绑当前CA证书？解绑后将无法进行签章等操作。', '提示', {
                confirmButtonText: '确认解绑',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                this.certInfo = null
                this.$message.success('已解绑')
            }).catch(() => { })
        }
    }
}
</script>

<style lang="less" scoped>
.ca-binding {
    padding: 20px;
    background-color: #f0f4fb;
    min-height: 100vh;

    .page-header {
        display: flex;
        align-items: center;

        .page-title {
            font-size: 20px;
            font-weight: bold;
            color: #333;
            margin: 0;
        }
    }

    .page-content {
        margin-top: 20px;
    }

    .binding-card {
        background: #fff;
        border-radius: 12px;
        border: 1px solid #e8eef7;
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
        overflow: hidden;

        &.bound {
            border-color: rgba(103, 194, 58, 0.25);
        }

        &.unbound {
            border-color: rgba(245, 108, 108, 0.25);
        }

        .card-header {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            padding: 18px 20px;
            background: linear-gradient(180deg, rgba(240, 244, 251, 0.9), rgba(255, 255, 255, 0));

            .card-title {
                font-size: 16px;
                font-weight: 700;
                color: #1f2d3d;
                line-height: 1.2;
            }

            .card-subtitle {
                margin-top: 6px;
                font-size: 12px;
                color: #8a95a7;
                line-height: 1.4;
            }

            .status-badge {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                padding: 6px 10px;
                border-radius: 999px;
                font-size: 12px;
                font-weight: 700;
                border: 1px solid transparent;

                &.bound {
                    color: #67c23a;
                    background: rgba(103, 194, 58, 0.12);
                    border-color: rgba(103, 194, 58, 0.22);
                }

                &.unbound {
                    color: #f56c6c;
                    background: rgba(245, 108, 108, 0.12);
                    border-color: rgba(245, 108, 108, 0.22);
                }
            }
        }

        .card-body {
            padding: 16px 20px 20px;

            .info-grid {
                display: grid;
                grid-template-columns: 160px 1fr;
                gap: 12px 18px;

                .info-item {
                    .label {
                        font-size: 12px;
                        color: #8a95a7;
                        margin-bottom: 6px;
                    }

                    .value {
                        font-size: 14px;
                        color: #1f2d3d;
                        font-weight: 600;
                        line-height: 1.6;
                        word-break: break-all;

                        &.muted {
                            color: #b0b7c3;
                            font-weight: 500;
                        }
                    }
                }
            }

            .action-row {
                margin-top: 16px;
                display: flex;
                align-items: center;
                justify-content: flex-end;
                gap: 10px;
            }

            .select-row {
                margin-top: 14px;
                display: flex;
                align-items: center;
                gap: 10px;

                .select-label {
                    font-size: 12px;
                    color: #8a95a7;
                    width: 60px;
                    flex-shrink: 0;
                }

                .select-tip {
                    font-size: 12px;
                    color: #b0b7c3;
                }
            }
        }
    }
}
</style>
